import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as eidAppointmentActions from '../../Redux/Actions/eidAppointmentDetails';
import { toastMessage } from '../../utils/toast';
import { dateTimeFormat } from '../../utils';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';

const NoteModal = ({ show, onHide, appointmentId }) => {
    const { t } = useTranslation();

    const [noteText, setNoteText] = useState('');
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [savingNote, setSavingNote] = useState(false);

    const resetState = () => {
        setNoteText('');
        setNotes([]);
        setLoadingNotes(false);
        setSavingNote(false);
    };

    const loadNotes = useCallback(async () => {
        if (!appointmentId) return;
        try {
            setLoadingNotes(true);
            const res = await eidAppointmentActions.eidAppointmentNoteGetByAppointmentId({ eidAppointmentId: appointmentId });
            if (res && res.data) {
                setNotes(res.data || []);
            } else {
                setNotes([]);
                console.log(res?.message || t('eidAppointmentDetails.manageEidAppointment.eidNotFound'));
            }
        } catch (e) {
            console.log(t('eidAppointmentDetails.manageEidAppointment.eidNotFound'));
        } finally {
            setLoadingNotes(false);
        }
    }, [appointmentId, t]); 

    useEffect(() => {
        if (show && appointmentId) {
            loadNotes();
        } else if (!show) {
            resetState();
        }
    }, [show, appointmentId, loadNotes]);

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!noteText.trim()) {
            toastMessage('error', "Please Fill The Note ");
            return;
        }
        try {
            setSavingNote(true);
            const res = await eidAppointmentActions.eidAppointmentNoteCreate({ eidAppointmentId: appointmentId, note: noteText });
            if (res && res.data) {
                toastMessage('success', t('eidAppointmentDetails.manageEidAppointment.noteAddedSuccessfully'));
                setNoteText('');
                await loadNotes();
            } else {
                toastMessage('error', res?.message || t('eidAppointmentDetails.manageEidAppointment.failedToAddNote'));
            }
        } catch (e) {
            toastMessage('error', t('eidAppointmentDetails.manageEidAppointment.failedToAddNote'));
        } finally {
            setSavingNote(false);
        }
    };

    const handleClose = () => {
        resetState();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="xl" style={{width: "100%"}} backdrop="static"  clickkeyboard={false}>
            <Modal.Header >
                <Modal.Title>{t("eidAppointmentDetails.manageEidAppointment.note")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleAddNote}>
                    <Form.Group controlId="noteText">
                        <Form.Label>{t("eidAppointmentDetails.manageEidAppointment.addNote")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder={t("eidAppointmentDetails.manageEidAppointment.enterNote")}
                        />
                    </Form.Group>

                    <div className="text-end mt-2">
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={savingNote || !noteText.trim()}
                        >
                            {savingNote ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" /> {t("eidAppointmentDetails.manageEidAppointment.saving")}
                                </>
                            ) : (
                                t("eidAppointmentDetails.manageEidAppointment.addNote")
                            )}
                        </Button>
                    </div>
                </Form>

                <hr />
                <h5 className="mb-3">{t("eidAppointmentDetails.manageEidAppointment.existingNotes")}</h5>
                <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                        <thead>
                            <tr>
                                <th>{t("eidAppointmentDetails.manageEidAppointment.sno")}</th>
                                <th>{t("eidAppointmentDetails.manageEidAppointment.lableNote")}</th>
                                <th>{t("eidAppointmentDetails.manageEidAppointment.createdOn")}</th>
                                <th>{t("eidAppointmentDetails.manageEidAppointment.createdBy")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notes.length > 0 ? (
                                notes.map((note, index) => (
                                    <tr key={note.id}>
                                        <td>{index + 1}</td>
                                        <td>{note.note}</td>
                                        <td>{note.createdOn}</td>
                                        <td>{note.createdBy ?? '-'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted">
                                        {t("eidAppointmentDetails.manageEidAppointment.noNotes")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </Modal.Body>

            <Modal.Footer>
                <Button variant="info" onClick={handleClose}>
                    {t("eidAppointmentDetails.manageEidAppointment.cancel")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default NoteModal;
